/**
 * A deneme's Tahmini puan, as a learner report prints it under the deneme's name.
 *
 * The backend computes the score on read from the official MEB/ÖSYM test weights and sends it on every
 * (deneme, ders) item of that deneme. This turns that list into display parts; callers translate keys.
 * Every score is shown (PO, Oct 2026) — a partial one is marked and names what it is missing.
 */
export interface EstimatedScore {
  scoreType: "LGS" | "TYT" | "SAY" | "EA" | "SOZ" | "DIL";
  value: number;
  complete: boolean;
  missingTyt?: boolean;
  missingTestList?: string[];
}

export interface EstimatedScoreLinePart {
  scoreType: string;
  /** "312,4" */
  valueText: string;
  partial: boolean;
  missingTyt: boolean;
  /** i18n keys (`test_TYT_FEN` …) of the tests the deneme did not contain. */
  missingTestKeyList: string[];
}

export const formatEstimatedScoreValue = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value) ? "—" : value.toFixed(1).replace(".", ",");

/** Display parts for an item's scores, or an empty list when the deneme has none. */
export const estimatedScoreLineParts = (item: { estimatedScoreDTOList?: EstimatedScore[] | null }): EstimatedScoreLinePart[] =>
  (item?.estimatedScoreDTOList ?? [])
    .filter((score) => score && Number.isFinite(score.value))
    .map((score) => ({
      scoreType: score.scoreType,
      valueText: formatEstimatedScoreValue(score.value),
      partial: !score.complete,
      missingTyt: !!score.missingTyt,
      missingTestKeyList: (score.missingTestList ?? []).map((test) => `test_${test}`),
    }));

/** The one score a deneme is charted by: its own biçim's score, or for an AYT its best field. */
export interface PrimaryEstimatedScore {
  scoreType: string;
  value: number;
  /** "381,6" */
  valueText: string;
  partial: boolean;
}

/**
 * LGS and TYT carry a single score, so it is that one. An AYT carries one per field (SAY/EA/SÖZ/DİL): the
 * chart shows the highest COMPLETE one, because a partial score is missing a whole test and would otherwise
 * win only for what it skipped; when every field is partial, the highest partial, marked as such. Null when
 * the deneme has no score at all (Diğer / no biçim).
 */
export const primaryEstimatedScore = (
  item: { estimatedScoreDTOList?: EstimatedScore[] | null } | null | undefined,
): PrimaryEstimatedScore | null => {
  const scoreList = (item?.estimatedScoreDTOList ?? []).filter((score) => score && Number.isFinite(score.value));
  if (scoreList.length === 0) return null;
  const completeList = scoreList.filter((score) => score.complete);
  const pool = completeList.length > 0 ? completeList : scoreList;
  const best = pool.reduce((top, score) => (score.value > top.value ? score : top));
  return {
    scoreType: best.scoreType,
    value: best.value,
    valueText: formatEstimatedScoreValue(best.value),
    partial: !best.complete,
  };
};

/**
 * The deneme whose score the summary circle shows: the most recent one that HAS a score (by `createdAt`,
 * which the list already resolves to the exam date). Null when no deneme in the list is scored.
 */
export const latestScoredDeneme = <T extends { createdAt?: number | null; estimatedScoreDTOList?: EstimatedScore[] | null }>(
  rowList: T[] | null | undefined,
): { row: T; score: PrimaryEstimatedScore } | null => {
  let latest: { row: T; score: PrimaryEstimatedScore } | null = null;
  (rowList ?? []).forEach((row) => {
    const score = primaryEstimatedScore(row);
    if (!score) return;
    if (!latest || (Number(row.createdAt) || 0) > (Number(latest.row.createdAt) || 0)) latest = { row, score };
  });
  return latest;
};

/** The national score scale every Tahmini puan sits on. */
export const ESTIMATED_SCORE_MIN = 100;
export const ESTIMATED_SCORE_MAX = 500;
